import { QueryKey, UseQueryOptions, UseSuspenseQueryOptions } from '@tanstack/react-query';

import { RequestData } from './hooks/types';
import { useApiGetRequest } from './hooks/useApiGetRequest';
import { useApiGetSuspenseRequest } from './hooks/useApiGetSuspenseRequest';

export function buildUseGetQuery<RequestPathVariables, RequestQueryParams, SuccessDto, ErrorDto>(
  maybeQueryKey:
    | QueryKey
    | ((
        p?: RequestData<RequestPathVariables, RequestQueryParams, undefined>,
        o?: Omit<UseQueryOptions<SuccessDto, ErrorDto>, 'queryKey' | 'queryFn'>,
      ) => QueryKey),
  path: string,
) {
  return (
    params?: RequestData<RequestPathVariables, RequestQueryParams, undefined>,
    options?: Omit<UseQueryOptions<SuccessDto, ErrorDto>, 'queryKey' | 'queryFn'>,
  ) => {
    const queryKey = typeof maybeQueryKey === 'function' ? maybeQueryKey(params, options) : maybeQueryKey;

    return useApiGetRequest<RequestPathVariables, RequestQueryParams, SuccessDto, ErrorDto>(
      queryKey,
      path,
      params,
      options,
    );
  };
}

/**
 * Same as buildUseGetQuery, but backed by useSuspenseQuery. Use for data that a page
 * already renders behind a Suspense boundary (see withSuspenseBoundary) so components
 * can rely on `data` always being defined instead of guarding against `undefined`/loading
 * states themselves.
 */
export function buildUseSuspenseGetQuery<RequestPathVariables, RequestQueryParams, SuccessDto, ErrorDto>(
  maybeQueryKey:
    | QueryKey
    | ((
        p?: RequestData<RequestPathVariables, RequestQueryParams, undefined>,
        o?: Omit<UseSuspenseQueryOptions<SuccessDto, ErrorDto>, 'queryKey' | 'queryFn'>,
      ) => QueryKey),
  path: string,
) {
  return (
    params?: RequestData<RequestPathVariables, RequestQueryParams, undefined>,
    options?: Omit<UseSuspenseQueryOptions<SuccessDto, ErrorDto>, 'queryKey' | 'queryFn'>,
  ) => {
    const queryKey = typeof maybeQueryKey === 'function' ? maybeQueryKey(params, options) : maybeQueryKey;

    return useApiGetSuspenseRequest<RequestPathVariables, RequestQueryParams, SuccessDto, ErrorDto>(
      queryKey,
      path,
      params,
      options,
    );
  };
}
